import {
  AdminInput,
  Button,
  Disclaimer,
  Div,
  ExpandEditorIcon,
  H3,
  Icon,
  Modal,
  P,
  lightTheme,
} from '@vgrubs/components';
import { ChangeEventHandler, useState } from 'react';

/**
 * A prompt textarea for the Menu-AI feature. It is drag-resizable (vertical),
 * shows a live character count, and has a small "Expand text" affordance that
 * opens a wide (max 1000px) modal with a big textarea bound to the SAME value —
 * so the modal is just a roomier editor for this one field (edits sync both
 * ways since `value`/`onChange` are shared).
 */
export const PromptTextarea = ({
  value,
  onChange,
  placeholder,
  label,
  description,
  $textareaHeight = 80,
  disabled,
  expandTitle = 'Edit prompt',
  expandZIndex = 200,
}: {
  value: string;
  onChange: ChangeEventHandler<HTMLTextAreaElement>;
  placeholder?: string;
  label?: string;
  // Helper text describing what this prompt is for; shown in the expand modal.
  description?: string;
  $textareaHeight?: number | string;
  disabled?: boolean;
  expandTitle?: string;
  expandZIndex?: number;
}) => {
  const [expandOpen, setExpandOpen] = useState(false);

  return (
    <Div $relative $width="100%">
      <AdminInput
        type="textarea"
        $resizable
        showCharCount
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        $textareaHeight={$textareaHeight}
        disabled={disabled}
        mb="8px"
      />

      <Div
        $flex
        $center
        $gap="4px"
        $pointer
        $mt="4"
        onClick={() => setExpandOpen(true)}
      >
        <Icon>
          <ExpandEditorIcon color={lightTheme.darkGray} />
        </Icon>
        <Disclaimer $m="0" $color={lightTheme.darkGray}>
          Expand editor
        </Disclaimer>
      </Div>

      <Modal
        isOpen={expandOpen}
        closeModal={() => setExpandOpen(false)}
        width="1000px"
        zIndex={expandZIndex}
        logo={false}
      >
        {' '}
        {expandTitle && (
          <H3 $mb="8" $left>
            {expandTitle}
          </H3>
        )}
        {description && (
          <P $mb="20" $left $color={lightTheme.lightGray}>
            {description}
          </P>
        )}
        <AdminInput
          type="textarea"
          $resizable
          showCharCount
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          $textareaHeight="400"
          disabled={disabled}
          mb="8px"
        />
        <Button
          $selected
          $w="100%"
          $mt="24"
          onClick={() => setExpandOpen(false)}
        >
          Done
        </Button>
      </Modal>
    </Div>
  );
};
